/**
 * Datos de "Mi horario" en Staff (008 · T095; contracts/staff-api.md S1, research R-25/R-26).
 *
 * `GET /attendance/days` con el rango **por defecto** del servidor (`[hoy − 13, hoy + 7]`): una sola
 * lectura alimenta la línea "Tu turno hoy" del Inicio, el estado de cada día de Asistencia y
 * "Próximos días" (misma clave de caché, R-26 "una sola llamada para Inicio y Asistencia"). Ruta
 * **autenticada** del realm staff (firma `X-Fermin-Device`); la respuesta se valida con
 * `StaffDaysResponseSchema` de `@fermin/contracts`. El cliente no calcula ningún estado: los decide
 * el servidor (`display_status`, `has_schedule`).
 *
 * `refetchOnWindowFocus: true` como el historial; nada se persiste en el teléfono (wiring §11.11).
 * `invalidateAttendanceViews` (history/api.ts) la invalida tras marcar, regularizar o justificar.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { StaffDaysResponseSchema, type StaffDaysResponse } from '@fermin/contracts';

import { api, type ApiError } from '../../api/client';

import { STAFF_DAYS_QUERY_KEY } from './keys';

export { STAFF_DAYS_QUERY_KEY };

export function fetchStaffDays(signal?: AbortSignal): Promise<StaffDaysResponse> {
  return api.get('/attendance/days', {
    schema: StaffDaysResponseSchema,
    ...(signal ? { signal } : {}),
  });
}

export interface StaffDaysQueryOptions {
  enabled?: boolean;
}

/** Días del trabajador (rango por defecto del servidor). */
export function useStaffDays(
  options: StaffDaysQueryOptions = {},
): UseQueryResult<StaffDaysResponse, ApiError> {
  return useQuery<StaffDaysResponse, ApiError>({
    queryKey: STAFF_DAYS_QUERY_KEY,
    queryFn: ({ signal }) => fetchStaffDays(signal),
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: true,
  });
}
